import type { User, UserStatus } from '@repo-level/core-front';
import { timestampByUTC } from '@repo-level/core-front';
import type {
  CMSCountryParams,
  CMSGroupParams,
  CMSOrder,
  CMSScheduler,
} from 'api/cms/base';
import { CMSGroupMode, EWeekday } from 'api/cms/base';
import moment from 'moment';
import { filter, findIndex, pipe, propEq } from 'ramda';

export const schedule = <
  T extends Pick<CMSGroupParams, 'durationFrom' | 'durationTo'>,
>({
  durationFrom,
  durationTo,
}: T) => {
  if (durationFrom == null && durationTo == null) return true;

  const currentTimestamp = new Date().getTime();

  if (durationFrom == null && durationTo != null) {
    const durationToTimestamp = timestampByUTC(durationTo);
    return currentTimestamp < durationToTimestamp;
  }

  if (durationFrom != null && durationTo == null) {
    const durationFromTimestamp = timestampByUTC(durationFrom);

    return durationFromTimestamp < currentTimestamp;
  }

  if (durationFrom != null && durationTo != null) {
    const durationFromTimestamp = timestampByUTC(durationFrom);
    const durationToTimestamp = timestampByUTC(durationTo);
    return (
      durationFromTimestamp < currentTimestamp &&
      currentTimestamp < durationToTimestamp
    );
  }

  return true;
};

const filterWithMode = <T>(
  array: Array<T>,
  mode: CMSGroupMode,
  fn: (value: T) => unknown
): boolean =>
  ({
    [CMSGroupMode.OR]: array.some(fn),
    [CMSGroupMode.AND]: array.every(fn),
  })[mode];

export const findGroup = (userGroups: UserStatus[]) => (group: string) =>
  findIndex(propEq('id', group))(userGroups) !== -1;

const filterByGroup = (
  groups: string[],
  mode: CMSGroupMode,
  userGroups: UserStatus[]
) => filterWithMode(groups, mode, findGroup(userGroups));

export const parseGroup = (groupString: string | null) => {
  if (groupString == null || groupString === '') return [];
  return groupString.replace(/\s/g, '').split(',');
};

export const groupsInclude =
  (user: User | undefined) =>
  <T extends CMSGroupParams>({
    showGuestToggle,
    groupsIncludeMode,
    groupsInclude,
  }: T) => {
    if (user?.statuses == null) return showGuestToggle;

    const parsedGroupsInclude = parseGroup(groupsInclude);

    if (parsedGroupsInclude.length === 0) return true;

    return filterByGroup(parsedGroupsInclude, groupsIncludeMode, user.statuses);
  };

export const groupsExclude =
  (user: User | undefined) =>
  <T extends CMSGroupParams>({
    groupsExcludeMode,
    groupsExclude,
    showGuestToggle,
  }: T) => {
    if (user?.statuses == null) return showGuestToggle;

    const parsedGroupsExclude = parseGroup(groupsExclude);

    if (parsedGroupsExclude.length === 0) return true;

    return !filterByGroup(
      parsedGroupsExclude,
      groupsExcludeMode,
      user.statuses
    );
  };

type FilterByGroupAndDurationParams = {
  user: User | undefined;
};

export const filterByGroupAndDuration = <T extends CMSGroupParams>({
  user,
}: FilterByGroupAndDurationParams) =>
  pipe<T[], T[], T[], T[]>(
    filter<T>(schedule),
    filter<T>(groupsInclude(user)),
    filter<T>(groupsExclude(user))
  );

export const filterByGroups = <T extends CMSGroupParams>({
  user,
}: FilterByGroupAndDurationParams) =>
  pipe<T[], T[], T[]>(
    filter<T>(groupsInclude(user)),
    filter<T>(groupsExclude(user))
  );

const arrayToUpperCase = (array: string[]) =>
  array.map((element) => element.toUpperCase());

export const countriesInclude =
  (countryCode?: string) =>
  <T extends CMSCountryParams>({
    countryCodesInclude,
    countryCodesExclude,
  }: T) => {
    if (!countryCode) return true;

    const parsedCountriesInclude = arrayToUpperCase(
      parseGroup(countryCodesInclude)
    );

    const parsedCountriesExclude = arrayToUpperCase(
      parseGroup(countryCodesExclude)
    );

    const isExcluded =
      parsedCountriesExclude.length !== 0
        ? parsedCountriesExclude.includes(countryCode)
        : false;

    const isIncluded =
      parsedCountriesInclude.length !== 0
        ? parsedCountriesInclude.includes(countryCode)
        : true;

    return !isExcluded && isIncluded;
  };

type FilterByCountryParams = {
  countryCode?: string;
};

export const filterByCountries = <T extends CMSCountryParams>({
  countryCode,
}: FilterByCountryParams) => filter<T>(countriesInclude(countryCode));

export const sortByOrder = <T extends CMSOrder>(array: T[]) =>
  array.sort(({ order: orderA }, { order: orderB }) => orderA - orderB);

export const weekdays: EWeekday[] = [
  EWeekday.Sunday,
  EWeekday.Monday,
  EWeekday.Tuesday,
  EWeekday.Wednesday,
  EWeekday.Thursday,
  EWeekday.Friday,
  EWeekday.Saturday,
];

export const filterBySchedule = <T extends CMSScheduler>(elements: T[]) => {
  const today = moment().utc().format('dddd') as EWeekday;

  return elements.filter((el) => {
    if (!el.scheduler?.length) return true;
    return el.scheduler.map(({ weekday }) => weekday).includes(today);
  });
};

export const replacePlaceholder = (
  str: string,
  placeholder: string,
  replacement: string
) => str.replace(`{{${placeholder}}}`, replacement);
